import { EditorContextHelper } from "@magnolia/template-annotations";
import React, { lazy } from "react";
import { Redirect } from "react-router";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { normalizeHref } from "domain/util/Magnolia";
import { recentExternalLink } from "state/recentExternalLink";

const LoadingIndicator = lazy(
  () => import("components/common/Basics/LoadingIndicator")
);

interface MagnoliaRedirectPageProps {
  linkTypepage: string;
  linkType: string;
  linkTypeexternal: string;
}
const RedirectPage = ({
  linkTypepage: link,
  linkType,
  linkTypeexternal,
}: MagnoliaRedirectPageProps) => {
  const recentExternalOpened = useRecoilValue(recentExternalLink);
  const setRecentExternalOpened = useSetRecoilState(recentExternalLink);

  const isExternal = linkType === "external";
  const inPreview = window.location.search.includes("mgnlPreview");
  const target = isExternal ? linkTypeexternal : normalizeHref(link);

  // Show an informational notice instead of performing the redirect when:
  //  - we're in the page editor (edit mode), for any redirect type -- otherwise the
  //    SPA navigates away and the author can never see or select this page; or
  //  - we're previewing an *external* redirect: opening a third-party site in a new
  //    tab (the window.open branch below) is disruptive and not a meaningful preview.
  // Internal redirects in preview still resolve to the real target page (see below).
  if (EditorContextHelper.inEditor() || (isExternal && inPreview)) {
    return (
      <div style={{ padding: "2rem", fontFamily: "sans-serif", lineHeight: 1.5 }}>
        <h1 style={{ margin: "0 0 0.5rem" }}>Redirect page</h1>
        <p style={{ margin: "0 0 0.25rem" }}>
          This page is not editable. On the public site visitors are redirected
          to:
        </p>
        <p style={{ margin: "0 0 1rem", fontWeight: "bold", wordBreak: "break-all" }}>
          {target || "(no redirect target configured)"}
        </p>
        <p style={{ margin: 0, color: "#666" }}>
          Edit the page properties to change the redirect target.
        </p>
      </div>
    );
  }

  if (isExternal) {
    let windowOpenReference = null;
    if (recentExternalOpened !== linkTypeexternal) {
      windowOpenReference = window.open(linkTypeexternal, "_blank");
      if (windowOpenReference !== null) {
        window.history.back();
      }
      setRecentExternalOpened(linkTypeexternal);
    }

    return <LoadingIndicator />;
  }

  // In Magnolia preview the iframe URL carries ?mgnlPreview=true. react-router's
  // <Redirect> otherwise drops the query string, and on admin-host environments
  // (stage/prod, hostname contains "admin") AdminRedirect then sees no mgnlPreview
  // and bounces the preview to AdminCentral. Carry the preview params over so the
  // target renders in preview, matching local behaviour.
  const search = inPreview ? window.location.search : "";

  return <Redirect to={{ pathname: target, search }} />;
};

export default RedirectPage;
